import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { getApiUrl } from "../../api";

interface QuickMenuItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface CompanyInfo {
  phone: string;
  operating_hours: string;
}

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

// 0=일 ~ 6=토 값을 월~일 순서로 정렬
const sortWeekdays = (weekdays: number[]) =>
  [...weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));

const makeIcon = (children: React.ReactNode) => (
  <svg viewBox="0 0 24 24" width={44} height={44} fill="none" stroke="#f5a623" strokeWidth={1.5}>
    {children}
  </svg>
);

const GUEST_ITEMS: QuickMenuItem[] = [
  {
    label: "인삿말",
    href: "/page/about_greeting",
    icon: makeIcon(<>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>),
  },
  {
    label: "마루빛 소개",
    href: "/page/about_introduce",
    icon: makeIcon(<>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </>),
  },
  {
    label: "프로그램 소개",
    href: "/program",
    icon: makeIcon(<>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>),
  },
  {
    label: "오시는길",
    href: "/page/location",
    icon: makeIcon(<>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>),
  },
];

const MEMBER_ITEMS: QuickMenuItem[] = [
  {
    label: "마루 포인트 내역",
    href: "/mypage/point",
    icon: makeIcon(<>
      <circle cx="12" cy="12" r="9" />
      <text x="12" y="17" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#f5a623" stroke="none">P</text>
    </>),
  },
  {
    label: "출석현황",
    href: "/mypage/attendance",
    icon: makeIcon(<>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M9 16l2 2 4-4" />
    </>),
  },
  {
    label: "프로그램 신청내역",
    href: "/program/check",
    icon: makeIcon(<>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>),
  },
  {
    label: "도서 대출/반납",
    href: "/book/loan",
    icon: makeIcon(<>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </>),
  },
];

export default function QuickMenu() {
  const { isLoggedIn } = useAuthStore();
  const items = isLoggedIn ? MEMBER_ITEMS : GUEST_ITEMS;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [closedWeekdays, setClosedWeekdays] = useState<number[] | null>(null);
  const [autoPublicHoliday, setAutoPublicHoliday] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiUrl("/marubit/api/company/detail"));
        if (!res.ok) return;
        const data = await res.json();
        setCompany(data);
      } catch (e) {
        console.error("회사 정보 로딩 실패:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiUrl("/marubit/api/holiday/weekdays"));
        if (!res.ok) return;
        const data = await res.json();
        setClosedWeekdays(data.closed_weekdays);
      } catch (e) {
        console.error("휴무요일 로딩 실패:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiUrl("/marubit/api/holiday/setting"));
        if (!res.ok) return;
        const data = await res.json();
        setAutoPublicHoliday(data.auto_public_holiday);
      } catch (e) {
        console.error("공휴일 설정 로딩 실패:", e);
      }
    })();
  }, []);

  const UsageInfo = ({ mobile = false }: { mobile?: boolean }) => (
      <div
        className={[
          "w-full rounded-2xl bg-white border-[2px] border-[#e8e0dc]",
          mobile
              ? "py-4"
              : "py-6 flex flex-wrap items-center justify-between gap-y-3"
        ].join(" ")}
        style={{ paddingLeft: "60px", paddingRight: "60px" }}
      >
      <div className={`flex items-center gap-3 ${mobile ? "mb-2" : ""}`}>
        <span className={`font-semibold text-[#887267] ${mobile ? "text-[12px]" : "text-[14px]"}`}>
          대표번호
        </span>
        <span className={`font-black text-[#5a4035] ${mobile ? "text-[18px]" : "text-[22px]"}`}>
          {company?.phone ?? "-"}
        </span>
      </div>
      <p
        className={[
          "m-0 text-[#666] leading-relaxed",
          mobile ? "mb-1 text-[12px]" : "text-[14px] pl-10 border-l border-[#e8e0dc]",
        ].join(" ")}
      >
        <span className="font-semibold text-[#887267]">운영시간</span>{mobile ? " : " : " "}
        {company?.operating_hours ?? "-"}
      </p>
      <p
        className={[
          "m-0 text-[#666] leading-relaxed",
          mobile ? "text-[12px]" : "text-[14px] pl-10 border-l border-[#e8e0dc]",
        ].join(" ")}
      >
        <span className="font-semibold text-[#887267]">휴무일</span>{mobile ? " : " : " "}
        {closedWeekdays === null ? (
          "-"
        ) : closedWeekdays.length > 0 || autoPublicHoliday ? (
          <>
            {[
              closedWeekdays.length > 0
                ? `매주 ${sortWeekdays(closedWeekdays).map(w => WEEKDAY_LABELS[w]).join("·")}요일`
                : null,
              autoPublicHoliday ? "공휴일" : null,
            ].filter(Boolean).join(", ")}{" "}
            <span className="text-red-500 font-semibold">휴무</span>
          </>
        ) : (
          "연중무휴"
        )}
      </p>
    </div>
  );

  const IconBox = ({ item }: { item: QuickMenuItem }) => (
    <Link
      to={item.href}
      className={[
        "no-underline flex flex-col items-center gap-[14px]",
        isMobile ? "w-[calc(50%-8px)]" : "flex-1",
      ].join(" ")}
    >
      <div
        className="flex items-center justify-center w-full rounded-[20px] border-[1.5px] border-[#fde9c0] bg-[#fffbf2] transition-all duration-150 hover:shadow-[0_4px_16px_rgba(245,166,35,0.2)] hover:border-[#f5a623]"
        style={{
          height: isMobile ? "100px" : "110px",
        }}
      >
        {item.icon}
      </div>
      <span className="text-[14px] font-semibold text-[#333] text-center">
        {item.label}
      </span>
    </Link>
  );

  if (isMobile) {
    return (
      <div className="bg-white px-5 py-7">
        <UsageInfo mobile />
        <div className="flex flex-wrap gap-4 mt-6">
          {items.map(item => <IconBox key={item.label} item={item} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-9">
      <div className="max-w-[1200px] mx-auto px-6 flex flex-col gap-8">
        <UsageInfo />
        <div className="flex justify-center gap-8">
          {items.map(item => <IconBox key={item.label} item={item} />)}
        </div>
      </div>
    </div>
  );
}